import {
  Action,
  createAction,
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
} from '@reduxjs/toolkit';
import JWTDecode from 'jwt-decode';

import {
  AuthApi,
  EntityApi,
  EntityInfo,
  FindOneOptions,
  PermissionType,
  ResetPasswordBody,
  UpdateEnterpriseInfoUserBody,
  UpdatePasswordBody,
} from 'api';
import { updateTokenBearer as updateTokenBearerBlogService } from 'api/services/blog';
import { updateTokenBearer as updateTokenBearerChatService } from 'api/services/chat';
import { updateTokenBearer as updateTokenBearerEntityService } from 'api/services/entity';
import { updateTokenBearer as updateTokenBearerIdentityService } from 'api/services/identity';
import { updateTokenBearer as updateTokenBearerNotificationService } from 'api/services/notification';
import { updateTokenBearer as updateTokenBearerStockService } from 'api/services/stock';
import { updateTokenBearer as updateTokenBearerUploadService } from 'api/services/upload';
import { REFRESH_TOKEN, SELECTED_ENTITY, USER_ID } from 'types/localStorage';

import { RootState } from '../app';

export type Credentials = {
  phoneNumber: string;
  password: string;
};

export type AccessToken = {
  accessToken: string;
};

export const createAuthSliceListenerMiddleware = createListenerMiddleware();

export const takeLastedRefreshAccessToken = createAction(
  'takeLastedRefreshAccessToken'
);

createAuthSliceListenerMiddleware.startListening({
  actionCreator: takeLastedRefreshAccessToken,
  effect: async (action: Action, listenerApi: any) => {
    // cancel other running instances
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(1000);
    listenerApi.dispatch(refreshAccessToken());
  },
});

export const changePassword = createAsyncThunk<
  any,
  UpdatePasswordBody,
  { rejectValue: { message: string } }
>('auth/changePassword', async (body, { rejectWithValue }) => {
  try {
    const response = await EntityApi.updatePassword(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const updateEntityInfo = createAsyncThunk<
  any,
  { id: string; body: EntityInfo },
  { rejectValue: { message: string } }
>('account/updateEntityInfo', async ({ id, body }, { rejectWithValue }) => {
  try {
    const response = await EntityApi.updateEntityInfo(id, body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findOneEntityWithAuth = createAsyncThunk<
  any,
  { id: string; options: FindOneOptions },
  { rejectValue: { message: string } }
>(
  'entity/findOneEntityWithAuth',
  async ({ id, options }, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findOneWithAuth(id, options);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const login = createAsyncThunk(
  'auth/loginForUser',
  async (credentials: any, { rejectWithValue }) => {
    try {
      let response;
      if (credentials.phoneNumber) {
        delete credentials.email;
        response = await AuthApi.login(credentials);
      } else if (credentials.email) {
        delete credentials.phoneNumber;
        response = await AuthApi.loginWithEmail(credentials);
      } else {
        throw Error('Invalid params!');
      }

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshAccessTokenForUser',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage?.getItem(REFRESH_TOKEN) || '';
      const response = await AuthApi.refreshAccessToken(refreshToken);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logoutForUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthApi.logout();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const resetPassword = createAsyncThunk<
  any,
  ResetPasswordBody,
  { rejectValue: { message: string } }
>('auth/resetPassword', async (body, { rejectWithValue }) => {
  try {
    const response = await AuthApi.resetPassword(body);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const findToCheckPhoneNumberExist = createAsyncThunk<
  any,
  {
    options: { phoneNumber: string; shouldReturnEntity?: boolean };
    activated?: boolean;
  },
  { rejectValue: { message: string } }
>(
  'auth/findToCheckPhoneNumberExist',
  async ({ options, activated = false }, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findToCheckPhoneNumberExist(
        options,
        activated
      );
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const findToCheckEmailExist = createAsyncThunk<
  any,
  { options: { email: string } },
  { rejectValue: { message: string } }
>('auth/findToCheckEmailExist', async ({ options }, { rejectWithValue }) => {
  try {
    const response = await EntityApi.findToCheckVerifiedEmailExist(options);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }
    return rejectWithValue(error.response.data);
  }
});

export const createUserForEmployee = createAsyncThunk(
  'auth/createUserForEmployee',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.createUserForEmployee(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createUser = createAsyncThunk(
  'auth/createUser',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await EntityApi.createUser(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEnterpriseInfoUser = createAsyncThunk(
  'auth/findEnterpriseInfoUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EntityApi.findEnterpriseInfoUser();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateEnterpriseInfoUser = createAsyncThunk(
  'auth/updateEnterpriseInfoUser',
  async (body: UpdateEnterpriseInfoUserBody, { rejectWithValue }) => {
    try {
      const response = await EntityApi.updateEnterpriseInfoUser(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface IUser {
  sub: string;
  fullName: string;
  displayName: string;
  avatar: string;
  email: string;
  phoneNumber: string;
}

interface IAuthItem {
  id: string;
  fullName: string;
  displayName: string;
  avatar: string;
  email: string;
  phoneNumber: string;
  domain?: string;
  isDiaryVerified?: boolean;
}

interface AuthState {
  canInit: boolean;
  isInitAuth: boolean;
  isAuthenticated: boolean;
  permissions: PermissionType[];
  isLogout: boolean;
  accessToken: any;
  refreshToken: any;
  isRefreshing: boolean;
  item: IAuthItem | any | undefined;
}

const initialState: AuthState = {
  canInit: false,
  isInitAuth: false,
  isAuthenticated: false,
  permissions: [],
  isLogout: false,
  accessToken: undefined,
  refreshToken: undefined,
  isRefreshing: false,
  item: undefined,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateCanInit(state, { payload }) {
      state.canInit = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(findOneEntityWithAuth.fulfilled, (state, action) => {
        const { permissions } = action.payload.data;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { accessToken } = action.payload.data;
        const {
          sub: id,
          fullName,
          displayName,
          avatar,
          email,
          phoneNumber,
        }: IUser = JWTDecode(accessToken);

        state.isAuthenticated = true;
        state.isLogout = false;
        state.accessToken = accessToken;
        state.item = {
          id,
          fullName,
          displayName,
          avatar,
          email,
          phoneNumber,
        };

        localStorage.setItem(USER_ID, id);
        // localStorage.setItem(REFRESH_TOKEN, id);
        updateTokenBearerBlogService(`Bearer ${accessToken}`);
        updateTokenBearerUploadService(`Bearer ${accessToken}`);
        updateTokenBearerEntityService(`Bearer ${accessToken}`);
        updateTokenBearerIdentityService(`Bearer ${accessToken}`);
        updateTokenBearerChatService(`Bearer ${accessToken}`);
        updateTokenBearerNotificationService(`Bearer ${accessToken}`);
        updateTokenBearerStockService(`Bearer ${accessToken}`);
      })
      .addCase(refreshAccessToken.pending, (state, action) => {
        state.isRefreshing = true;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        const { accessToken } = action.payload.data;
        const {
          sub: id,
          fullName,
          displayName,
          avatar,
          email,
          phoneNumber,
        }: IUser = JWTDecode(accessToken);

        state.isRefreshing = false;
        state.isAuthenticated = true;
        state.isLogout = false;
        state.accessToken = accessToken;
        state.item = {
          id,
          fullName,
          displayName,
          avatar,
          email,
          phoneNumber,
        };

        localStorage.setItem(USER_ID, id);
        // localStorage.setItem(REFRESH_TOKEN, id);
        updateTokenBearerBlogService(`Bearer ${accessToken}`);
        updateTokenBearerUploadService(`Bearer ${accessToken}`);
        updateTokenBearerEntityService(`Bearer ${accessToken}`);
        updateTokenBearerIdentityService(`Bearer ${accessToken}`);
        updateTokenBearerChatService(`Bearer ${accessToken}`);
        updateTokenBearerNotificationService(`Bearer ${accessToken}`);
        updateTokenBearerStockService(`Bearer ${accessToken}`);
        state.isInitAuth = true;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.isLogout = true;
        state.accessToken = undefined;
        state.refreshToken = undefined;
        state.item = undefined;
        state.isInitAuth = true;
        state.isRefreshing = false;

        localStorage.removeItem(USER_ID);
        localStorage.removeItem(SELECTED_ENTITY);
        localStorage.removeItem(REFRESH_TOKEN);

        updateTokenBearerBlogService('');
        updateTokenBearerStockService('');
        updateTokenBearerUploadService('');
        updateTokenBearerEntityService('');
        updateTokenBearerIdentityService('');
        updateTokenBearerChatService('');
        updateTokenBearerNotificationService('');
      })
      .addCase(logout.fulfilled, state => {
        state.isAuthenticated = false;
        state.isLogout = true;
        state.accessToken = undefined;
        state.refreshToken = undefined;
        state.item = undefined;

        localStorage.removeItem(USER_ID);
        localStorage.removeItem(SELECTED_ENTITY);
        localStorage.removeItem(REFRESH_TOKEN);

        updateTokenBearerBlogService('');
        updateTokenBearerStockService('');
        updateTokenBearerUploadService('');
        updateTokenBearerEntityService('');
        updateTokenBearerIdentityService('');
        updateTokenBearerChatService('');
        updateTokenBearerNotificationService('');
      });
    builder.addCase(updateEntityInfo.fulfilled, (state, { payload }) => {
      const { data } = payload;
      if (data.id === state.item?.id) {
        state.item = { ...state.item, ...data };
      }
    });
  },
});

const { actions, reducer: authReducer } = authSlice;

export const { updateCanInit } = actions;

export default authReducer;

export const selectAuth = (state: RootState): AuthState =>
  state.auth as AuthState;
